import { loadAllStaff } from "@/redux/rtk/features/hrm/user/userSlice";
import { useAddDealMutation, useGetDealSuggestionsQuery } from "@/redux/rtk/features/CRM/deal/dealApi";
import { useGetDealStagesQuery } from "@/redux/rtk/features/CRM/dealStage/dealStageApi";
import { addSingleAttachment } from "@/redux/rtk/features/CRM/attachment/attachmentSlice";
import { useGetUniversitiesQuery } from "@/redux/rtk/features/university/universityApi";
import { useGetMajorsQuery } from "@/redux/rtk/features/major/majorApi";
import { useGetDegreesQuery } from "@/redux/rtk/features/degree/degreeApi";
import { Form, Input, Select, message, DatePicker, Upload, Button, AutoComplete, Mentions } from "antd";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { getSetting } from "@/redux/rtk/features/setting/settingSlice";
import dayjs from "dayjs";
import { UploadOutlined, FileSearchOutlined, LoadingOutlined } from "@ant-design/icons";
import axios from "axios";
import { NATIONALITIES } from "@/utils/nationalities";
import Card from "@/UI/Card";
import LeadsCreateMany from "../../Lead/Leads/LeadsCreateMany";

export default function AddDeal({ onClose }) {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const [uploading, setUploading] = useState(false);
  const userId = localStorage.getItem("id");

  const { list: staffList } = useSelector((state) => state.users);
  const { data: dealStages, isLoading: dealStageLoading } = useGetDealStagesQuery();
  const { data: universities } = useGetUniversitiesQuery();
  const { data: majors } = useGetMajorsQuery();
  const { data: degrees, isLoading: degreeLoading } = useGetDegreesQuery();

  const { data: setting } = useSelector((state) => state.setting);
  const [fieldConfig, setFieldConfig] = useState({});

  useEffect(() => {
    dispatch(loadAllStaff({ query: "all" }));
    if (!setting) {
      dispatch(getSetting());
    }
  }, [dispatch, setting]);

  useEffect(() => {
    if (setting?.dealFieldConfig) {
      try {
        setFieldConfig(JSON.parse(setting.dealFieldConfig));
      } catch (e) {
        console.error("Error parsing deal field config", e);
      }
    }
  }, [setting]);

  const isRequired = (key) => {
    return fieldConfig[key] === undefined ? true : fieldConfig[key];
  };

  // Set default deal owner (current user) and default stage
  useEffect(() => {
    if (userId) {
      form.setFieldsValue({
        dealOwnerId: parseInt(userId),
      });
    }
  }, [userId, form]);

  useEffect(() => {
    if (dealStages && dealStages.length > 0) {
      const defaultStage = dealStages.find(s => s.stageName?.toLowerCase() === "new") || dealStages[0];
      if (defaultStage) {
        form.setFieldsValue({
          dealStageId: defaultStage.id,
        });
      }
    }
  }, [dealStages, form]);


  const [addDeal, { isLoading }] = useAddDealMutation();
  const [firstNameKey, setFirstNameKey] = useState("");
  const [lastNameKey, setLastNameKey] = useState("");

  const { data: firstNameSuggestions } = useGetDealSuggestionsQuery(
    { field: "firstName", key: firstNameKey },
    { skip: !firstNameKey }
  );
  const { data: lastNameSuggestions } = useGetDealSuggestionsQuery(
    { field: "lastName", key: lastNameKey },
    { skip: !lastNameKey }
  );

  const [isScanning, setIsScanning] = useState(false);

  const handleScan = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setIsScanning(true);
    const formData = new FormData();
    formData.append("file", file);

    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(`${import.meta.env.VITE_APP_API}/ocr/scan`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          "Authorization": `Bearer ${token}`
        },
      });

      const data = response.data.data;
      if (data) {
        // Robust name mapping
        const fullNameEn = data.full_name_english || data.full_name || data.full_name_en || "";
        const firstName = data.given_names || (fullNameEn ? fullNameEn.split(" ")[0] : "");
        const lastName = data.surname || (fullNameEn ? fullNameEn.split(" ").slice(1).join(" ") : "");

        // Robust Gender mapping
        let gender = undefined;
        const sexVal = (data.sex || data.sex_arabic || data.gender || "").toString().toLowerCase();
        if (sexVal.startsWith("m") || sexVal.includes("ذكر")) {
          gender = "Male";
        } else if (sexVal.startsWith("f") || sexVal.includes("أنثى")) {
          gender = "Female";
        }

        // Robust Date format handling
        let dob = undefined;
        const dobStr = data.date_of_birth || data.dateOfBirth;
        if (dobStr) {
          dob = dayjs(dobStr, "DD/MM/YYYY");
          if (!dob.isValid()) dob = dayjs(dobStr);
        }

        // Map data to form fields
        const updateValues = {
          firstName: firstName,
          lastName: lastName,
          nationality: data.nationality_english || data.nationality || data.nationality_arabic || undefined,
          gender: gender,
          dateOfBirth: dob && dob.isValid() ? dob : undefined,
        };

        // Add Passport Number
        const passNum = data.passport_number || data.national_id || data.national_id_number;
        if (passNum) {
          updateValues.passportNumber = passNum;
        }

        // Collect all extra info for Description
        const currentDescription = form.getFieldValue("description") || "";
        const extraInfo = [];
        if (data.profession || data.profession_arabic || data.profession_english) {
          const prof = [data.profession, data.profession_arabic, data.profession_english].filter(Boolean).join(" / ");
          extraInfo.push(`Profession: ${prof}`);
        }
        if (data.issuing_country || data.issuing_state) extraInfo.push(`Country: ${data.issuing_country || data.issuing_state}`);
        if (data.full_name_arabic) extraInfo.push(`Name (Ar): ${data.full_name_arabic}`);
        if (data.address || data.address_arabic) extraInfo.push(`Address: ${data.address || data.address_arabic}`);
        if (data.military_status) extraInfo.push(`Military: ${data.military_status}`);

        const idInfo = extraInfo.length > 0 ? `\n--- OCR INFO ---\n${extraInfo.join("\n")}` : "";
        if (idInfo) {
          updateValues.description = currentDescription ? `${currentDescription}${idInfo}` : idInfo.trim();
        }

        form.setFieldsValue(updateValues);
        message.success(t("lead.scanSuccess"));
      }
    } catch (error) {
      console.error("Scan error:", error);
      message.error(t("lead.scanError"));
    } finally {
      setIsScanning(false);
      e.target.value = null;
    }
  };

  const onFinish = async (values) => {
    try {
      const formData = {
        ...values,
        dealName: `${values.firstName} ${values.lastName}`,
        dateOfBirth: values.dateOfBirth ? dayjs(values.dateOfBirth).format("YYYY-MM-DD") : null,
        contactStatusId: 1,
      };

      const resp = await addDeal(formData);
      if (resp.data) {
        const dealId = resp.data.id;
        const attachments = values.attachments || [];

        if (attachments.length > 0) {
          setUploading(true);
          try {
            const uploadPromises = attachments.map(item => {
              if (!item.file || item.file.length === 0) return null;

              const file = item.file[0].originFileObj;
              const attachmentData = new FormData();
              attachmentData.append('files[]', file);
              attachmentData.append('dealId', dealId);
              attachmentData.append('attachmentType', 'document');
              attachmentData.append('attachmentName', item.name === 'other' ? item.customName : item.name);

              if (values.dealOwnerId) {
                attachmentData.append('attachmentOwnerId', values.dealOwnerId);
              }
              return dispatch(addSingleAttachment(attachmentData)).unwrap();
            }).filter(p => p !== null);

            await Promise.all(uploadPromises);
            message.success(t("deal.uploadSuccess"));
          } catch (uploadError) {
            console.error("Upload error:", uploadError);
            message.warning(t("deal.uploadError"));
          } finally {
            setUploading(false);
          }
        } else {
          message.success(t("success.created"));
        }

        form.resetFields();
        if (onClose && typeof onClose === 'function') {
          onClose();
        }
      }
    } catch (error) {
      console.error("Error creating deal:", error);
      message.error(t("errors.somethingWentWrong"));
      setUploading(false);
    }
  };

  const nationalities = NATIONALITIES;

  return (
    <>
      <Form
        form={form}
        onFinish={onFinish}
        onFinishFailed={(errorInfo) => {
          console.error("Form Validation Failed:", errorInfo);
          message.error(t("form.validationFailed") || "Please fill in all required fields!");
        }}
        layout="vertical"
        className="p-1"
      >
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold">{t("deal.addDeal")}</h2>
          <div className="flex gap-2">
            <label className="cursor-pointer bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md transition-colors flex items-center gap-2 text-sm font-medium">
              {isScanning ? <LoadingOutlined /> : <FileSearchOutlined />}
              {isScanning ? t("lead.scanning") : t("lead.scanDocument")}
              <input
                type="file"
                accept="image/*,.pdf"
                onChange={handleScan}
                className="hidden"
              />
            </label>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4">
          <Form.Item
            label={t("deal.dealOwner")}
            name="dealOwnerId"
            hidden
            rules={[{ required: isRequired("dealOwnerId"), message: t("deal.selectOwner") }]}
          >
            <Select
              showSearch
              placeholder={t("deal.selectOwner")}
              filterOption={(input, option) =>
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
              options={staffList?.map(user => ({
                value: user.id,
                label: `${user.firstName} ${user.lastName}`,
              }))}
            />
          </Form.Item>

          <Form.Item
            label={t("deal.stage")}
            name="dealStageId"
            hidden
            rules={[{ required: isRequired("dealStageId"), message: t("deal.selectStatus") }]}
          >
            <Select
              loading={dealStageLoading}
              placeholder={t("deal.selectStatus")}
              options={dealStages?.map(stage => ({
                value: stage.id,
                label: stage.stageName,
              }))}
            />
          </Form.Item>

          <Form.Item
            label={t("common.firstName")}
            name="firstName"
            rules={[{ required: isRequired("firstName"), message: t("common.firstNameRequired") }]}
          >
            <AutoComplete
              options={firstNameSuggestions || []}
              onSearch={(val) => setFirstNameKey(val)}
              onSelect={(val) => form.setFieldsValue({ firstName: val })}
            >
              <Input placeholder="John" />
            </AutoComplete>
          </Form.Item>

          <Form.Item
            label={t("common.lastName")}
            name="lastName"
            rules={[{ required: isRequired("lastName"), message: t("common.lastNameRequired") }]}
          >
            <AutoComplete
              options={lastNameSuggestions || []}
              onSearch={(val) => setLastNameKey(val)}
              onSelect={(val) => form.setFieldsValue({ lastName: val })}
            >
              <Input placeholder="Doe" />
            </AutoComplete>
          </Form.Item>

          <Form.Item
            label={t("application.nationality")}
            name="nationality"
            rules={[{ required: isRequired("nationality"), message: t("form.required") }]}
          >
            <Select
              showSearch
              placeholder={t("application.selectNationality")}
              options={nationalities}
            />
          </Form.Item>

          <Form.Item
            label={t("application.passportNumber")}
            name="passportNumber"
            rules={[{ required: isRequired("passportNumber"), message: t("form.required") }]}
          >
            <Input placeholder="A1234567" />
          </Form.Item>

          <Form.Item
            label={t("common.phone")}
            name="phone"
            normalize={(value) => value?.replace(/[^0-9+]/g, "")}
            rules={[{ required: isRequired("phone"), message: t("form.required") }, { pattern: /^\+?[0-9]+$/, message: t("common.numericPlusOnly") }]}
          >
            <Input placeholder="+1 273 642 1785" />
          </Form.Item>

          <Form.Item
            label={t("common.mobileWhatsApp")}
            name="mobile"
            normalize={(value) => value?.replace(/[^0-9+]/g, "")}
            rules={[{ required: isRequired("mobile"), message: t("form.required") }, { pattern: /^\+?[0-9]+$/, message: t("common.numericPlusOnly") }]}
          >
            <Input placeholder="+1 273 642 1785" />
          </Form.Item>

          <Form.Item
            label={t("application.dateOfBirth")}
            name="dateOfBirth"
            rules={[{ required: isRequired("dateOfBirth"), message: t("form.required") }]}
          >
            <DatePicker className="w-full" />
          </Form.Item>

          <Form.Item
            label={t("application.gender")}
            name="gender"
            rules={[{ required: isRequired("gender"), message: t("form.required") }]}
          >
            <Select placeholder={t("application.selectGender")}>
              <Select.Option value="Male">{t("application.male")}</Select.Option>
              <Select.Option value="Female">{t("application.female")}</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            label={t("lead.degree") || "Degree"}
            name="degree"
            rules={[{ required: isRequired("degree"), message: t("form.required") }]}
          >
            <Select
              placeholder={t("lead.degree") || "Select Degree"}
              loading={degreeLoading}
              allowClear
              showSearch
            >
              {degrees?.map((item) => (
                <Select.Option key={item.id} value={item.name}>
                  {item.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label={t("application.registrationStatus")}
            name="registrationStatus"
            className="md:col-span-2"
            rules={[{ required: isRequired("registrationStatus"), message: t("form.required") }]}
          >
            <Select placeholder={t("application.selectRegistrationStatus")}>
              <Select.Option value="Registered">{t("application.registered")}</Select.Option>
              <Select.Option value="Not Registered">{t("application.notRegistered")}</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            label={t("lead.selectUniversity") || "Select University"}
            name="universityIds"
            className="md:col-span-2"
            rules={[{ required: isRequired("universityIds"), message: t("form.required") }]}
          >
            <Select
              mode="multiple"
              showSearch
              placeholder={t("lead.selectUniversity") || "Select Universities"}
              filterOption={(input, option) =>
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
              options={(universities?.universities ?? universities ?? []).map(u => ({
                value: u.id,
                label: u.universityName,
              }))}
            />
          </Form.Item>

          <Form.Item
            label={t("lead.major") || "Major / Specialization"}
            name="majorId"
            rules={[{ required: isRequired("majorId"), message: t("form.required") }]}
          >
            <Select
              showSearch
              allowClear
              placeholder={t("lead.selectMajor") || "Select Major"}
              filterOption={(input, option) =>
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
              options={(majors ?? []).map(m => ({
                value: m.id,
                label: m.name,
              }))}
            />
          </Form.Item>
          <Form.Item
            label={t("deal.uploadStatus")}
            name="uploadStatus"
            initialValue="pending"
            rules={[{ required: isRequired("uploadStatus"), message: t("form.required") }]}
          >
            <Select placeholder={t("deal.uploadStatus")}>
              <Select.Option value="pending">{t("deal.pending")}</Select.Option>
              <Select.Option value="done">{t("deal.done")}</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            label={t("deal.priority")}
            name="priority"
            initialValue="Direct"
            rules={[{ required: isRequired("priority"), message: t("deal.selectPriority") }]}>
            <Select placeholder={t("deal.selectPriority")}>
              <Select.Option value="Direct">{t("deal.direct")}</Select.Option>
              <Select.Option value="Transfer">{t("deal.transfer")}</Select.Option>
            </Select>
          </Form.Item>



          <div className="md:col-span-2">
            <Form.List name="attachments">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...restField }) => (
                    <div key={key} className="flex gap-4 items-start mb-4 border p-4 rounded bg-gray-50 relative">
                      <Form.Item
                        {...restField}
                        name={[name, 'name']}
                        label={t("application.attachmentTitle")}
                        rules={[{ required: true, message: t("application.missingTitle") }]}
                        className="flex-1 mb-0"
                      >
                        <Select placeholder={t("application.attachmentTitlePlaceholder")}>
                          {[
                            "passport",
                            "high_school_transcript",
                            "bachelor_degree_certificate",
                            "bachelor_degree_transcript",
                            "course_description",
                            "master_degree_certificate",
                            "master_degree_transcript",
                            "english_proficient_certificate",
                            "conditional_acceptance_letter",
                            "final_letter",
                            "ticket",
                            "payment",
                            "conditional_acceptance",
                            "student_letter",
                            "other"
                          ].map((type) => (
                            <Select.Option key={type} value={type === 'other' ? 'other' : t(`attachment.${type}`)}>
                              {t(`attachment.${type}`)}
                            </Select.Option>
                          ))}
                        </Select>
                      </Form.Item>
                      <Form.Item
                        noStyle
                        shouldUpdate={(prevValues, curValues) =>
                          prevValues.attachments?.[name]?.name !== curValues.attachments?.[name]?.name
                        }
                      >
                        {({ getFieldValue }) =>
                          getFieldValue(['attachments', name, 'name']) === 'other' ? (
                            <Form.Item
                              {...restField}
                              name={[name, 'customName']}
                              label={t("attachment.namePlaceholder")}
                              rules={[{ required: true, message: t("attachment.nameRequired") }]}
                              className="flex-1 mb-0"
                            >
                              <Input placeholder={t("attachment.namePlaceholder")} />
                            </Form.Item>
                          ) : null
                        }
                      </Form.Item>
                      <Form.Item
                        {...restField}
                        name={[name, 'file']}
                        rules={[{ required: true, message: t("application.missingFile") }]}
                        label={t("application.file")}
                        className="flex-1 mb-0"
                        valuePropName="fileList"
                        getValueFromEvent={(e) => {
                          if (Array.isArray(e)) return e;
                          return e?.fileList;
                        }}
                      >
                        <Upload borderUpload={() => false} maxCount={1}>
                          <Button icon={<UploadOutlined />}>{t("application.selectFile")}</Button>
                        </Upload>
                      </Form.Item>
                      <Button
                        type="text"
                        danger
                        onClick={() => remove(name)}
                        className="mt-8"
                      >
                        {t("common.remove")}
                      </Button>
                    </div>
                  ))}
                  <Form.Item>
                    <Button type="dashed" onClick={() => add()} block icon={<UploadOutlined />}>
                      {t("application.addAttachment")}
                    </Button>
                  </Form.Item>
                </>
              )}
            </Form.List>
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-4">
          <Button onClick={onClose}>{t("common.cancel")}</Button>
          <Button type="primary" htmlType="submit" loading={isLoading || uploading}>
            {t("common.create")}
          </Button>
        </div>
      </Form>
      <Card
        className="my-5"
        title={<span className="text-center font-bold">{t("lead.importFromCSV")}</span>}>
        <LeadsCreateMany
          urlPath={"deal"}
          demoData={[
            [
              "Deal Name",
              "Contact Name*",
              "Deal Owner*",
              "Stage*",
              "Major",
              "University",
              "Country",
              "Nationality",
              "Created Time",
            ],
            [
              "Danya Sawan",
              "Danya Sawan",
              staffList?.[0]?.username || "admin",
              dealStages?.[0]?.stageName || "New",
              "Computer Science",
              "NEU;KYRENIA",
              "Turkey",
              "Jordan",
              "2023-01-01 10:00:00",
            ],
          ]}
          invalidTags={["Deals"]}
          title={t("deal.demoDeals") || "demo_deals"}
        />
      </Card>
    </>
  );
}
